import { useEffect, useState } from "react";
import { Button } from "@component/button";
import { Badge } from "@component/badge";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@component/dropdown-menu";
import { EmptyState } from "@component/empty-state";
import LoadingBox from "@component/loading-box";
import AddReviewerDialog from "@container/add-reviewer-dialog";
import { listReviewers, removeReviewer, resendReviewerInvitation, revokeReviewerSessions } from "@service/reviewer";
import { WorkspaceReviewer } from "@type/reviewer";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { parseFormat } from "@util/date";
import { MoreHorizontalIcon, UserCheckIcon, UsersIcon } from "lucide-react";

// TODO: remove once BE is live
const MOCK_REVIEWERS: WorkspaceReviewer[] = [
    {
        id: "mock-1",
        email: "james.tan@valiram.com.my",
        added_by: null,
        session_duration_days: 30,
        is_active: true,
        created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        active_session_count: 1,
    },
    {
        id: "mock-2",
        email: "priya.sharma@hiring.co",
        added_by: null,
        session_duration_days: 14,
        is_active: true,
        created_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
        active_session_count: 0,
    },
    {
        id: "mock-3",
        email: "michael.wong@partnerco.com",
        added_by: null,
        session_duration_days: 90,
        is_active: true,
        created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        active_session_count: 2,
    },
];

interface WorkspaceReviewersSectionProps {
    workspaceId: string;
    canManage: boolean;
}

export default function WorkspaceReviewersSection({
    workspaceId,
    canManage,
}: WorkspaceReviewersSectionProps) {
    const snackbar = useSnackbar();
    const [isLoading, setIsLoading] = useState(true);
    const [reviewers, setReviewers] = useState<WorkspaceReviewer[]>([]);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

    const onLoad = async () => {
        setIsLoading(true);
        try {
            const resp = await listReviewers(workspaceId);
            if (resp.code === 200 && resp.data) {
                setReviewers(resp.data);
            } else {
                setReviewers(MOCK_REVIEWERS);
            }
        } catch {
            // BE not yet implemented — use mock data for UI preview
            setReviewers(MOCK_REVIEWERS);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (workspaceId) {
            onLoad();
        }
    }, [workspaceId]);

    const handleResend = async (reviewer: WorkspaceReviewer) => {
        try {
            const resp = await resendReviewerInvitation(workspaceId, reviewer.id);
            if (resp.code !== 200) {
                snackbar.showError("Failed to resend invitation. Please try again.");
                return;
            }
            snackbar.showSuccess(`Invitation resent to ${reviewer.email}.`);
        } catch (error) {
            snackbar.showError("Failed to resend invitation. Please try again.");
            catchErrorLog(`resendReviewerInvitation error: ${error}`);
        }
    };

    const handleRevoke = async (reviewer: WorkspaceReviewer) => {
        try {
            const resp = await revokeReviewerSessions(workspaceId, reviewer.id);
            if (resp.code !== 200) {
                snackbar.showError("Failed to revoke access. Please try again.");
                return;
            }
            snackbar.showSuccess(`Access revoked for ${reviewer.email}.`);
            onLoad();
        } catch (error) {
            snackbar.showError("Failed to revoke access. Please try again.");
            catchErrorLog(`revokeReviewerSessions error: ${error}`);
        }
    };

    const handleRemove = async (reviewer: WorkspaceReviewer) => {
        try {
            const resp = await removeReviewer(workspaceId, reviewer.id);
            if (resp.code !== 200) {
                snackbar.showError("Failed to remove reviewer. Please try again.");
                return;
            }
            snackbar.showSuccess(`${reviewer.email} has been removed.`);
            onLoad();
        } catch (error) {
            snackbar.showError("Failed to remove reviewer. Please try again.");
            catchErrorLog(`removeReviewer error: ${error}`);
        }
    };

    return (
        <>
            <div className="pt-10 mt-10 border-t border-gray-200 dark:border-gray-700 space-y-6">
                <div className="flex items-start justify-between">
                    <div className="space-y-1">
                        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                            Workspace Reviewers
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Reviewers can only see candidate profiles shared directly with them.
                            They do not have platform access.
                        </p>
                    </div>
                    {canManage && (
                        <Button
                            onClick={() => setIsAddDialogOpen(true)}
                            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 flex-shrink-0 ml-4"
                        >
                            <UserCheckIcon className="h-4 w-4" />
                            Add Reviewer
                        </Button>
                    )}
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full min-w-max">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Email
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Date Added
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Session Length
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Status
                                </th>
                                {canManage && (
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        &nbsp;
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={canManage ? 5 : 4}>
                                        <LoadingBox />
                                    </td>
                                </tr>
                            ) : reviewers.length > 0 ? (
                                reviewers.map((reviewer) => (
                                    <tr
                                        key={reviewer.id}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                    >
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {reviewer.email}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-center text-gray-500 dark:text-gray-400">
                                            {reviewer.created_at
                                                ? parseFormat(reviewer.created_at, "D MMM YYYY")
                                                : "—"}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-center text-gray-500 dark:text-gray-400">
                                            {reviewer.session_duration_days} days
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <Badge
                                                variant="secondary"
                                                className={
                                                    reviewer.is_active && reviewer.active_session_count > 0
                                                        ? "bg-green-100 text-green-800 hover:bg-green-100 shadow-none"
                                                        : "bg-yellow-100 text-yellow-800 hover:bg-yellow-100 shadow-none"
                                                }
                                                style={{ borderRadius: "0.75rem", paddingTop: "5px", paddingBottom: "5px" }}
                                            >
                                                {reviewer.is_active && reviewer.active_session_count > 0
                                                    ? "Active"
                                                    : "Invited"}
                                            </Badge>
                                        </td>
                                        {canManage && (
                                            <td className="px-4 py-3 text-center">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                                            <MoreHorizontalIcon className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        {!(reviewer.is_active && reviewer.active_session_count > 0) && (
                                                            <DropdownMenuItem
                                                                onClick={() => handleResend(reviewer)}
                                                            >
                                                                Resend invitation
                                                            </DropdownMenuItem>
                                                        )}
                                                        <DropdownMenuItem
                                                            onClick={() => handleRevoke(reviewer)}
                                                        >
                                                            Revoke access
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            className="text-red-600"
                                                            onClick={() => handleRemove(reviewer)}
                                                        >
                                                            Remove reviewer
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={canManage ? 5 : 4} className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                        <EmptyState
                                            icon={<UsersIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />}
                                            title="No reviewers yet."
                                            description={
                                                canManage
                                                    ? <>Add a reviewer to share candidate profiles with external Hiring Managers.</>
                                                    : <>No reviewers have been added to this workspace.</>
                                            }
                                        />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <AddReviewerDialog
                open={isAddDialogOpen}
                onOpenChange={setIsAddDialogOpen}
                workspaceId={workspaceId}
                onSuccess={onLoad}
            />
        </>
    );
}
